import { Grid } from "@mui/material";
import { FormikProps } from "formik";
import CustomSelect from "../../../../../../../../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../../../../components/Text";
import StyledButtonV2 from "../../../../../../../../../../../../components/app/StyledButtonV2";
import StyledFormModal from "../../../../../../../../../../../../components/globalModal/StyledModal";
import { DisclosureHost } from "../../../../../../../../../../../../hooks/use-disclosure";
import useFormDraft from "../../../../../../../../../../../../hooks/use-form-draft";
import { Form2Payload } from "../../../../../../../../../scheduling/appointment/encounter/encounter-constant";
import {
  MSE_NOT_ASSESSED,
  MSE_OVERALL_NOTE_KEY,
  PROGRESS_MSE_FIELDS,
  PROGRESS_MSE_FIELD_KEYS,
} from "../ProgressNoteConstant";

interface ProgressMentalStatusModalProps {
  disclosureHost: DisclosureHost;
  formik: FormikProps<Form2Payload>;
}

/**
 * Current Mental Status — one single-select per component, so this
 * modal uses a dropdown grid rather than the checkbox cards the Psychotherapy /
 * DAP / BIRP MSE modal uses.
 *
 * "All Normal" and "All Not Assessed" fill every field in one click; both write
 * into the draft, so they are only committed to the note when Save is clicked.
 */
const ProgressMentalStatusModal = ({
  disclosureHost,
  formik,
}: ProgressMentalStatusModalProps) => {
  // Edits stay in the draft until Save, so the note behind the modal is
  // untouched while the user is still filling it in.
  const draft = useFormDraft(
    formik,
    PROGRESS_MSE_FIELD_KEYS,
    disclosureHost.opened
  );

  // Fills every Mental Status field with the given value — the field's own
  // configured "Normal" value, or the shared "Not Assessed" one.
  const fillAll = (
    pick: (field: (typeof PROGRESS_MSE_FIELDS)[number]) => string
  ) =>
    PROGRESS_MSE_FIELDS.forEach((field) =>
      draft.setFieldValue(field.key, pick(field))
    );

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={() => disclosureHost.close()}
      dialogTitle="Current Mental Status"
      dialogAction={
        <>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={() => disclosureHost.close()}
          />
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={() => {
              draft.save();
              disclosureHost.close();
            }}
            sx={{ width: "105px" }}
          />
        </>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "65vw",
          height: "80vh",
        },
      }}
    >
      <Flex flexDirection="column" gap={2} width="100%" pt={2}>
        <Flex gap={1} flexWrap="wrap" alignItems="center">
          <StyledButtonV2
            label="All Normal"
            variant="outlined"
            sx={{ width: "140px" }}
            onClick={() => fillAll((field) => field.normalValue)}
          />
          <StyledButtonV2
            label="All Not Assessed"
            variant="outlined"
            sx={{ width: "170px" }}
            onClick={() => fillAll(() => MSE_NOT_ASSESSED)}
          />
        </Flex>

        <Grid container spacing={2}>
          {PROGRESS_MSE_FIELDS.map((field) => (
            <Grid
              key={field.key}
              size={{
                xs: 12,
                sm: 6,
                md: 4,
              }}
            >
              <Text mb={1}>{field.label}</Text>
              <CustomSelect
                // The dropdown is rendered inside a modal, so it must not be
                // portaled out of the dialog (ECRH-4466).
                disablePortal
                clearable
                disableSortOptions
                name={field.key}
                value={(draft.values?.[field.key] as string) ?? ""}
                options={field.options}
                placeholder={`Select ${field.label}`}
                onChange={(value) =>
                  draft.setFieldValue(field.key, value ?? "")
                }
              />
            </Grid>
          ))}

          <Grid size={12}>
            <CustomTextArea
              label="Overall MSE Note"
              name={MSE_OVERALL_NOTE_KEY}
              placeholder="Additional mental status observations..."
              value={draft.values?.mseOverallNote || ""}
              onChange={draft.handleChange}
              minRows={3}
            />
          </Grid>
        </Grid>
      </Flex>
    </StyledFormModal>
  );
};

export default ProgressMentalStatusModal;
